import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  getContentEntityListsLookups,
  getContentEntityListsDatatable,
  getContentEntityListsByContentEntityId,
  getContentEntityListItems,
  createContentEntityList,
  editContentEntityList,
  getContentEntityListById,
  setContentEntityListsItemList,
} from './thunks';
import { ContentEntityListsState } from './types';

export const name = 'contentEntityLists';

const initialState: ContentEntityListsState = {
  lookups: {
    data: [],
    status: 'idle',
  },
  contentEntityListsByContentEntityId: {
    data: [],
    status: 'idle',
  },
  contentEntityListItems: {
    data: [],
    status: 'idle',
  },
  setContentEntityListsItemList: {
    status: 'idle',
  },
  datatable: {
    data: null,
    status: 'idle',
  },
  create: {
    data: null,
    status: 'idle',
  },
  edit: {
    data: null,
    status: 'idle',
  },
  byId: {
    data: null,
    status: 'idle',
  },
};

export const getContentEntityListsLookupsAsync = createAsyncThunk(
  name + '/getContentEntityListsLookups',
  getContentEntityListsLookups
);

export const getContentEntityListsByContentEntityIdAsync = createAsyncThunk(
  name + '/getContentEntityListsByContentEntityId',
  getContentEntityListsByContentEntityId
);

export const getContentEntityListItemsAsync = createAsyncThunk(
  name + '/getContentEntityListItems',
  getContentEntityListItems
);

export const setContentEntityListsItemListAsync = createAsyncThunk(
  name + '/setContentEntityListsItemList',
  setContentEntityListsItemList
);

export const getContentEntityListsDatatableAsync = createAsyncThunk(
  name + '/getContentEntityListsDatatable',
  getContentEntityListsDatatable
);

export const createContentEntityListAsync = createAsyncThunk(
  name + '/createContentEntityList',
  createContentEntityList
);

export const editContentEntityListAsync = createAsyncThunk(
  name + '/editContentEntityList',
  editContentEntityList
);

export const getContentEntityListByIdAsync = createAsyncThunk(
  name + '/getContentEntityListById',
  getContentEntityListById
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearContentEntityListsByContentEntityId: (state) => {
      state.contentEntityListsByContentEntityId = {
        data: [],
        status: 'idle',
      };
    },
    clearContentEntityListById: (state) => {
      state.byId = {
        data: null,
        status: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getContentEntityListsLookupsAsync.pending, (state) => {
        state.lookups.status = 'loading';
      })
      .addCase(getContentEntityListsLookupsAsync.fulfilled, (state, action) => {
        state.lookups = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getContentEntityListsLookupsAsync.rejected, (state) => {
        state.lookups.status = 'failed';
      })
      .addCase(getContentEntityListsByContentEntityIdAsync.pending, (state) => {
        state.contentEntityListsByContentEntityId.status = 'loading';
      })
      .addCase(
        getContentEntityListsByContentEntityIdAsync.fulfilled,
        (state, action) => {
          state.contentEntityListsByContentEntityId = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(
        getContentEntityListsByContentEntityIdAsync.rejected,
        (state) => {
          state.contentEntityListsByContentEntityId.status = 'failed';
        }
      )
      .addCase(getContentEntityListItemsAsync.pending, (state) => {
        state.contentEntityListItems.status = 'loading';
      })
      .addCase(getContentEntityListItemsAsync.fulfilled, (state, action) => {
        state.contentEntityListItems = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getContentEntityListItemsAsync.rejected, (state) => {
        state.contentEntityListItems.status = 'failed';
      })
      .addCase(setContentEntityListsItemListAsync.pending, (state) => {
        state.setContentEntityListsItemList.status = 'loading';
      })
      .addCase(setContentEntityListsItemListAsync.fulfilled, (state) => {
        state.setContentEntityListsItemList = {
          status: 'idle',
        };
      })
      .addCase(setContentEntityListsItemListAsync.rejected, (state) => {
        state.setContentEntityListsItemList.status = 'failed';
      })
      .addCase(getContentEntityListsDatatableAsync.pending, (state) => {
        state.datatable.status = 'loading';
      })
      .addCase(
        getContentEntityListsDatatableAsync.fulfilled,
        (state, action) => {
          state.datatable = { data: action.payload, status: 'idle' };
        }
      )
      .addCase(getContentEntityListsDatatableAsync.rejected, (state) => {
        state.datatable.status = 'failed';
      })
      .addCase(createContentEntityListAsync.pending, (state) => {
        state.create.status = 'loading';
      })
      .addCase(createContentEntityListAsync.fulfilled, (state, action) => {
        state.create = { data: action.payload, status: 'idle' };
      })
      .addCase(createContentEntityListAsync.rejected, (state) => {
        state.create.status = 'failed';
      })
      .addCase(editContentEntityListAsync.pending, (state) => {
        state.edit.status = 'loading';
      })
      .addCase(editContentEntityListAsync.fulfilled, (state, action) => {
        state.edit = { data: action.payload, status: 'idle' };
      })
      .addCase(editContentEntityListAsync.rejected, (state) => {
        state.edit.status = 'failed';
      })
      .addCase(getContentEntityListByIdAsync.pending, (state) => {
        state.byId.status = 'loading';
      })
      .addCase(getContentEntityListByIdAsync.fulfilled, (state, action) => {
        state.byId = { data: action.payload, status: 'idle' };
      })
      .addCase(getContentEntityListByIdAsync.rejected, (state) => {
        state.byId.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
